import { useQuery } from "@apollo/client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Warning from "@mui/icons-material/Warning";
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { EncampyPortalLoginLauncher } from "components/Encampy/PortalLoginLauncher";
import { GeneratePdfButton } from "components/GeneratePdfButton";
import { CredentialFieldsDisplay } from "components/PortalCredential/CredentialFieldsDisplay";
import { findAlternateId } from "encamp-shared/src/facilityAlternateId/findAlternateId";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { GetTierIiReportQuery } from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { GET_STEERS_CREDENTIALS_PAGE_DATA } from "routes/Staff/SteersCredentials/api";
import { CredentialVerification } from "./CredentialVerification";
import { FieldDisplay } from "./shared/FieldComponents";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

const VerificationSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

type FacilityAlternateId = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["facility"]["facilityAlternateIds"]
>[number];

export function StatePortal() {
  const theme = useTheme();
  const alerts = useAlerts();
  const { data } = useFulfillmentDetails();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-state-portal-expanded",
    { defaultValue: true }
  );

  const portalCredential = data?.tierIIReport.facility.portalCredential;
  const facilityAlternateIds = data?.tierIIReport.facility.facilityAlternateIds;
  const reportingYear = data?.tierIIReport.reportingYear;
  const reportKind = data?.tierIIReport.reportKind;

  const isTexas = data?.tierIIReport.facility.state === "TX";

  const credentialsContent = (
    <CredentialFieldsDisplay
      credential={portalCredential?.credential}
      portalUrl={portalCredential?.url}
      showCombinedInvoicing={data?.tierIIReport.facility.state === "WV"}
    />
  );

  const verificationContent = portalCredential && (
    <VerificationSection>
      <CredentialVerification
        credential={portalCredential.credential}
        facilityId={data.tierIIReport.facility.id ?? ""}
      />
    </VerificationSection>
  );

  // Just the verification status field for collapsed mode
  const verificationStatusOnly = portalCredential?.credential && (
    <FieldDisplay
      label="Verification Status:"
      value={prettyPrintEnumValue(
        portalCredential.credential.verificationStatus
      )}
      noWrap
    />
  );

  const actionButtons = (
    <Stack direction="row" spacing={1} data-testid="action-buttons">
      <EncampyPortalLoginLauncher
        portalCredential={portalCredential}
        facilityAlternateIds={facilityAlternateIds}
        reportingYear={reportingYear}
        reportKind={reportKind}
        onSuccess={(message) => alerts.success(message)}
        onError={(error) => alerts.error(error)}
        size="medium"
        disabled={
          !portalCredential?.credential?.username ||
          !portalCredential?.credential?.password
        }
        disabledReason={
          !portalCredential?.credential?.username ||
          !portalCredential?.credential?.password
            ? "Portal credentials are required for portal login"
            : undefined
        }
      />
      <GeneratePdfButton
        reportId={data?.tierIIReport.id ?? ""}
        label="Download Options"
        includeTierIISubmit={true}
        reportOrgStatus={data?.tierIIReport.organizationStatus}
        disabled={!data?.tierIIReport.id?.length}
        size="small"
        endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
      />
    </Stack>
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3, 2, 3)}
    >
      {/* Header with title and expand/collapse button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography variant="h6" data-testid="state-portal-title">
            State Portal
          </Typography>
          <IconButton size="small">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Stack>

      {/* Show only verification status when collapsed */}
      {!isExpanded && verificationStatusOnly && (
        <Box sx={{ pl: 0 }}>{verificationStatusOnly}</Box>
      )}

      <Collapse in={isExpanded}>
        <Grid container spacing={3}>
          {/* Credentials content */}
          <Grid size={12}>
            {isTexas ? (
              <TexasCredentialsContent
                facilityAlternateIds={facilityAlternateIds ?? []}
              />
            ) : (
              credentialsContent
            )}
          </Grid>

          {/* Verification content (not shown for Texas) */}
          {!isTexas && <Grid size={12}>{verificationContent}</Grid>}

          {/* Action buttons at bottom */}
          <Grid size={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              {actionButtons}
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Stack>
  );
}

/** For Texas, displays
 * - Error if the facility does not have a CN
 * - Number of associated credentials for a given STEERS CN
 * - Link to the STEERS Credentials page for the given CN
 *  */
function TexasCredentialsContent({
  facilityAlternateIds,
}: {
  facilityAlternateIds: FacilityAlternateId[];
}) {
  const steersCn = findAlternateId(facilityAlternateIds, ["STEERS_CN"])?.value;

  const { data: steersData } = useQuery(GET_STEERS_CREDENTIALS_PAGE_DATA, {
    variables: { cn: steersCn },
    skip: !steersCn,
  });

  // When filtered by CN, there will be at most one mapping
  const cnMapping = steersData?.steersCredentialsPageData.mappings[0];
  const credentialCount = cnMapping?.credentialCount ?? 0;

  if (!steersCn) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Warning color="warning" sx={{ fontSize: 18 }} />
        <Typography variant="body2" color="text.secondary">
          Facility must have a STEERS CN for credential associations
        </Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Link
        href={`/staff/steers-credentials?search=${encodeURIComponent(
          steersCn
        )}&associatedCredentials=true`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
      >
        Manage Texas STEERS credential associations
        <OpenInNew sx={{ fontSize: 14 }} />
      </Link>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {credentialCount === 0 && (
          <Warning color="warning" sx={{ fontSize: 14 }} />
        )}
        <Typography
          variant="caption"
          color={credentialCount === 0 ? "warning.main" : "text.secondary"}
        >
          {credentialCount} credential{credentialCount !== 1 ? "s" : ""}{" "}
          associated with {steersCn}
        </Typography>
      </Stack>
    </Box>
  );
}
